import { makeStyles, Theme } from "@material-ui/core/styles";
import { COLORS } from "config/theme/baseTheme";

export interface ScrollableImageListStyleProps {
  imageWidth: number;
  imageHeight: number;
  zoomLevel: number;
}

export default makeStyles<Theme, ScrollableImageListStyleProps>((theme) => ({
  root: {
    width: "100%",
    position: "relative",
  },
  scrollContainer: {
    display: "flex",
    overflowX: "auto",
    scrollbarWidth: "none",
    width: (props) => props.imageWidth * 6 + 42,
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  image: {
    minWidth: (props) => props.imageWidth,
    height: (props) => props.imageHeight,
    marginRight: 8,
    borderRadius: 2,
    cursor: "pointer",
    transition: "box-shadow 0.2s",
    boxShadow: "none",
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  zoomOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: COLORS.OVERLAY_BACKGROUND,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageZoomed: {
    boxShadow: "0 0 8px rgba(0,0,0,0.2)",
  },
  // These CSS values are the *max bound* / fallback (used until the image's
  // natural size is known) - always an explicitly-sized box (never
  // "auto"/shrink-wrap) at every zoom level, so no percentage-resolution
  // circularity. Once natural size is known, ScrollableImageList.tsx
  // overrides width/height inline to the image's actual rendered footprint
  // (fitBoxPx), capped at this same max bound, so this wrapper's corner - and
  // therefore the close button anchored to it - always sits exactly at the
  // real edge of the visible image, not floating off in empty letterboxed
  // space. Bounded to the dialog's own content width (%, never vw) and vh
  // (safe since the dialog spans the full viewport height), so it can never
  // render outside the side dialog. The overlay itself is deliberately kept
  // small (see zoomOverlay) so it doesn't block the rest of the accordion, so
  // this wrapper - not the overlay - is what the close button is anchored to.
  zoomedImageWrapper: {
    position: "relative",
    width: (props) => (props.zoomLevel > 0 ? "92%" : "55%"),
    height: (props) => (props.zoomLevel > 0 ? "88vh" : "48vh"),
  },
  // The clipped, pannable viewport - 100% of zoomedImageWrapper, which always
  // has an explicit size, so this never hits percentage-resolution circularity.
  // Rounded here (not just on the image): once zoomed/panned, the visible
  // rectangle is defined by this element's overflow:hidden clip, not by the
  // image's own (usually off-screen once panned) corners, so this is where
  // the rounding actually has to live to be visible.
  zoomStage: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: (props) => (props.zoomLevel > 0 ? "hidden" : "visible"),
    touchAction: (props) => (props.zoomLevel > 0 ? "none" : "auto"),
    borderRadius: 8,
  },
  // width/height:auto + max-width/max-height:100% (not width/height:100% +
  // objectFit:contain) so the <img> element's own box shrink-wraps to the
  // actual visible, aspect-correct image content instead of always being the
  // full (possibly letterboxed) stage rectangle. This matters for pan
  // clamping: it measures this element's getBoundingClientRect(), which must
  // reflect the real image bounds or the clamp lets you drag past the real
  // edges into empty space. zoomStage now always has an explicit, non-auto
  // size (never "auto"/shrink-wrap) at every level, so this two-level
  // percentage chain can't hit the earlier circularity bug.
  zoomedImage: {
    display: "block",
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "100%",
    borderRadius: 8,
    userSelect: "none",
    touchAction: "none",
  },
  // A sibling of zoomStage (not a child of it), so it can never be affected
  // by the image's transform or clipped by the stage's overflow:hidden.
  // Pinned to the top-right corner of zoomedImageWrapper - the box whose
  // bounds actually match the visible zoomed image - so it is always exactly
  // at the top-right corner of the zoomed view, at every zoom level.
  closeZoomButton: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 9999,
    background: "rgba(255,255,255,0.85)",
    "&:hover": {
      background: "rgba(255,255,255,1)",
    },
  },
  arrowButton: {
    margin: "0 8px",
  },
  arrowButtonLeft: {
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 20,
  },
  arrowButtonRight: {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translate(35%, -50%)",
    zIndex: 20,
   
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  placeholder: {
    width: (props) => props.imageWidth,
    height: (props) => props.imageHeight,
    backgroundColor: "#e0e0e0",
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
  },
}));